import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={heroRef}
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ background: '#0A0A0A' }}
    >
      {/* Background */}
      <div className="absolute inset-0">
        {/* Deep dark base with subtle radial vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 50% 40%, #111111 0%, #0A0A0A 55%, #060606 100%)',
          }}
        />
        {/* Subtle noise texture layer */}
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
            backgroundRepeat: 'repeat',
            backgroundSize: '256px 256px',
          }}
        />
      </div>

      {/* Animated grid */}
      <div className="absolute inset-0 hero-grid opacity-40 pointer-events-none" />

      {/* Accent blobs — radial-gradient, no filter: blur() paint cost */}
      <div
        className="absolute top-1/3 left-1/4 w-96 h-96 pointer-events-none animate-float"
        style={{ background: 'radial-gradient(circle, rgba(180,255,57,0.09) 0%, transparent 70%)' }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-80 h-80 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,240,255,0.07) 0%, transparent 70%)' }}
      />

      {/* Main content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24 pb-16">
        <div className="text-center">
          {/* Pre-headline badge */}
          <div
            className={`inline-flex items-center gap-2 border border-ds-red/40 px-3 sm:px-4 py-2 mb-8 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ background: 'rgba(180,255,57,0.08)' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-ds-red animate-pulse flex-shrink-0" />
            <span className="font-body text-ds-red text-xs tracking-[0.15em] sm:tracking-[0.3em] uppercase">
              Darkside Digital — Tourism Website &amp; SEO Specialists
            </span>
          </div>

          {/* Main Headline — SEO keyword H1 */}
          {/* min-height = 3 lines × font-size × line-height (0.92) = 2.76 × clamped size.        */}
          {/* This reserves the exact vertical space Bebas Neue will occupy once loaded, so       */}
          {/* the fallback font expanding to 4+ lines doesn't cause a downward CLS shift when     */}
          {/* the display font finally snaps the text back to 3 lines.                            */}
          <h1
            className={`font-display text-white leading-none mb-3 transition-all duration-1000 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{
              fontSize: 'clamp(1.8rem, 5.5vw, 6.5rem)',
              lineHeight: '0.92',
              letterSpacing: '0.03em',
              minHeight: 'calc(2.76 * clamp(1.8rem, 5.5vw, 6.5rem))',
            }}
          >
            AI-POWERED WEBSITES &amp;
            <br />
            <span className="text-ds-red neon-text-red">LOCAL SEO</span> FOR
            <br />
            AUSTRALIAN BUSINESSES
          </h1>

          {/* Brand punch line — H2 */}
          {/* min-height = 1 line × font-size × line-height (1.0, leading-none)                 */}
          {/* Reserves the exact Bebas Neue 1-line height so a wider fallback font wrapping to   */}
          {/* 2 lines doesn't cause a CLS shift when Bebas Neue snaps it back to 1 line.         */}
          <h2
            className={`font-display text-white/55 leading-none mb-4 transition-all duration-1000 delay-300 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ fontSize: 'clamp(1rem, 2.6vw, 3rem)', letterSpacing: '0.06em', minHeight: 'clamp(1rem, 2.6vw, 3rem)' }}
          >
            Your competitors are already scared.
          </h2>

          {/* Subheadline body */}
          <p
            className={`font-body text-white/85 text-base sm:text-xl md:text-2xl max-w-2xl mx-auto mt-6 mb-8 sm:mt-8 sm:mb-10 leading-relaxed transition-all duration-1000 delay-400 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            Based in Mission Beach, Queensland — we specialise in websites, SEO, and AI search for <strong className="text-white">tourism and hospitality businesses</strong> across Far North QLD and all of Australia. Darkside Digital builds high-performance websites and deploys AI to put
            local businesses on top — and <strong className="text-white">keep them there.</strong>
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4 transition-all duration-1000 delay-600 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <button
              onClick={() => handleScroll('#contact')}
              className="btn-red text-sm gap-2 hero-cta-glow w-full sm:w-auto justify-center"
            >
              <span>Get Your Free AI Visibility Audit</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </button>
          </div>
        </div>

        {/* Bottom quick stats */}
        <div
          className={`mt-16 max-w-3xl mx-auto transition-all duration-1000 delay-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Green accent line — ties back to CTA and badge */}
          <div className="h-px bg-gradient-to-r from-ds-red via-ds-red/50 to-transparent mb-0" />

          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 divide-x divide-ds-border/60">
            {[
              { num: '11+', label: 'Years In Business' },
              { num: '126+', label: 'Local Businesses' },
              { num: '#1', label: 'Google Ranking' },
              { num: '3x', label: 'Lead Increase' },
            ].map((stat) => (
              <div key={stat.label} className="px-8 py-7 text-center">
                <div
                  className="font-display text-6xl text-ds-red leading-none mb-2"
                  style={{ letterSpacing: '0.03em' }}
                >
                  {stat.num}
                </div>
                <div className="font-body text-white/55 text-xs tracking-[0.2em] uppercase">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          <div className="h-px bg-ds-border/40" />
        </div>

        {/* GEO Teaser Banner */}
        <div
          className={`mt-3 max-w-3xl mx-auto transition-all duration-1000 delay-1100 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <Link
            to="/geo-aeo"
            className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 w-full border border-ds-red/30 bg-ds-red/6 px-4 sm:px-5 py-3.5 hover:bg-ds-red/12 hover:border-ds-red/60 transition-all duration-300 cursor-pointer"
            style={{ boxShadow: '0 0 24px rgba(180,255,57,0.10)' }}
          >
            {/* Left side */}
            <div className="flex items-center gap-3 min-w-0">
              <span className="relative flex h-2 w-2 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ds-red opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-ds-red" />
              </span>
              <span className="font-body text-white/75 text-xs leading-tight">
                Someone in your area just asked ChatGPT who to call. <strong className="text-white">Did it name you — or your competitor?</strong>
              </span>
            </div>
            {/* Right CTA */}
            <div className="flex items-center gap-1.5 flex-shrink-0 text-ds-red group-hover:gap-2.5 transition-all duration-300 pl-5 sm:pl-0">
              <span className="font-display text-xs tracking-widest uppercase whitespace-nowrap">Free AI Visibility Audit</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform duration-200" />
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-1000 delay-1200 ${loaded ? 'opacity-60' : 'opacity-0'}`}
      >
        <span className="font-body text-white/40 text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/30 to-transparent" />
      </div>
    </section>
  );
}
